import React, { useMemo } from "react";

import {
  CadenceFilterValue,
  classifyAccountStatus,
  hasOrgRequestPending,
  hasPendingFormal,
} from "./assessmentStatusFilter";
import type { PortfolioAccount } from "../../Store/Reducers/CustomerWorkspaceSlice";

interface AssessmentStatusOverviewCardsProps {
  accounts: PortfolioAccount[];
  activeFilter?: CadenceFilterValue;
  onFilterClick?: (filter: CadenceFilterValue) => void;
}

const CARD_DEFS: { key: CadenceFilterValue; label: string }[] = [
  { key: "all", label: "Total Accounts" },
  { key: "not_assessed", label: "Unassessed" },
  { key: "stale", label: "Stale" },
  { key: "current", label: "Current" },
  { key: "pending", label: "Pending" },
];

/** Unassessed / Stale / Current → account cadence; Pending → open Formal/Team cycle. */
const AssessmentStatusOverviewCards: React.FC<
  AssessmentStatusOverviewCardsProps
> = ({ accounts, activeFilter, onFilterClick }) => {
  const counts = useMemo(() => {
    const base: Record<CadenceFilterValue, number> = {
      all: accounts.length,
      current: 0,
      stale: 0,
      not_assessed: 0,
      pending: 0,
    };
    accounts.forEach(account => {
      base[classifyAccountStatus(account.org_assessment_status)] += 1;
      if (hasOrgRequestPending(account) || hasPendingFormal(account)) {
        base.pending += 1;
      }
    });
    return base;
  }, [accounts]);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {CARD_DEFS.map(card => (
        <button
          key={card.key}
          type="button"
          onClick={() => {
            if (!onFilterClick) return;
            if (card.key !== "all" && activeFilter === card.key) {
              onFilterClick("all");
              return;
            }
            onFilterClick(card.key);
          }}
          className={`rounded-md border px-4 py-3 text-left transition-colors ${
            activeFilter === card.key
              ? "border-purple-500 bg-violet-50"
              : "border-gray-200 bg-white hover:bg-gray-50"
          }`}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {card.label}
          </p>
          <p className="mt-1 text-xl font-semibold text-gray-900">
            {counts[card.key]}
          </p>
        </button>
      ))}
    </div>
  );
};

export default AssessmentStatusOverviewCards;
